import { promptToPlainText, applyCanvasReferenceCommandV3 } from "@seedance/contracts";
import { MarkerType, type Edge, type Node, type Viewport } from "@xyflow/react";
import type { CanvasDocumentV3 as CanvasDocument, CanvasEdge, CanvasNodeV3 as CanvasNode } from "@seedance/contracts";
import { canvasCopy } from "../../copy/zh-CN.js";

export interface CanvasFlowNodeData extends Record<string, unknown> {
  canvasNode: CanvasNode;
  label: string;
}

export interface CanvasFlowEdgeData extends Record<string, unknown> {
  canvasEdge: CanvasEdge;
}

export interface CanvasFlowState {
  schemaVersion: CanvasDocument["schemaVersion"];
  nodes: Array<Node<CanvasFlowNodeData>>;
  edges: Array<Edge<CanvasFlowEdgeData>>;
  viewport: Viewport;
  groups: CanvasDocument["groups"];
}

function nodeLabel(node: CanvasNode): string {
  if (node.kind === "text") return promptToPlainText(node.text) || canvasCopy.emptyNote;
  if (node.kind === "script") return promptToPlainText(node.title) || canvasCopy.manualScript;
  if (node.kind === "seedance") return canvasCopy.seedanceNode;
  return node.kind === "image"
    ? canvasCopy.imageNode
    : node.kind === "video"
      ? canvasCopy.videoNode
      : canvasCopy.audioNode;
}

export function canvasDocumentToFlow(document: CanvasDocument): CanvasFlowState {
  return {
    schemaVersion: document.schemaVersion,
    nodes: document.nodes.map((canvasNode) => ({
      id: canvasNode.id,
      type: "default",
      position: canvasNode.position,
      width: canvasNode.size.width,
      height: canvasNode.size.height,
      data: { canvasNode, label: nodeLabel(canvasNode) }
    })),
    edges: document.edges.map((canvasEdge) => ({
      id: canvasEdge.id,
      source: canvasEdge.sourceNodeId,
      target: canvasEdge.targetNodeId,
      data: { canvasEdge }
    })),
    viewport: document.viewport,
    groups: document.groups
  };
}

export function flowToCanvasDocument(flow: CanvasFlowState, previous?: CanvasDocument): CanvasDocument {
  const removedNodes = previous?.nodes.filter(node => !flow.nodes.some(item => item.id === node.id)).map(node => node.id) ?? [];
  const removedEdges = previous?.edges.filter(edge => !flow.edges.some(item => item.id === edge.id)).map(edge => edge.id) ?? [];
  let reconciled = previous;
  if (reconciled && removedNodes.length) reconciled = applyCanvasReferenceCommandV3(reconciled,{type:"delete_nodes",nodeIds:removedNodes});
  if (reconciled && removedEdges.length) reconciled = applyCanvasReferenceCommandV3(reconciled,{type:"delete_edges",edgeIds:removedEdges});
  const changedGraph = reconciled !== previous;
  const reconciledNodes = new Map(reconciled?.nodes.map(node => [node.id,node]));
  return {
    schemaVersion: flow.schemaVersion,
    viewport: flow.viewport,
    nodes: flow.nodes.map((node) => {
      const canvasNode = changedGraph ? reconciledNodes.get(node.id) ?? node.data.canvasNode : node.data.canvasNode;
      const width = node.width ?? node.data.canvasNode.size.width;
      const height = node.height ?? node.data.canvasNode.size.height;
      if (canvasNode.id === node.id && canvasNode.position.x === node.position.x && canvasNode.position.y === node.position.y &&
          canvasNode.size.width === width && canvasNode.size.height === height) return canvasNode;
      return { ...canvasNode, id: node.id, position: node.position, size: { width, height } };
    }),
    edges: flow.edges.map((edge) => {
      const canvasEdge = edge.data!.canvasEdge;
      if (canvasEdge.id === edge.id && canvasEdge.sourceNodeId === edge.source && canvasEdge.targetNodeId === edge.target) return canvasEdge;
      return { ...canvasEdge, id: edge.id, sourceNodeId: edge.source, targetNodeId: edge.target };
    }),
    groups: changedGraph ? reconciled!.groups : flow.groups
  };
}

/** Only reuse immutable values with exactly the same fields; never retain stale UI flags. */
function reuseShallow<T extends object>(next: T, previous: T | undefined): T {
  if (!previous) return next;
  const keys = Object.keys(next) as Array<keyof T>;
  return keys.length === Object.keys(previous).length && keys.every(key => Object.hasOwn(previous, key) && Object.is(next[key], previous[key]))
    ? previous : next;
}

export function mergeCanvasDocumentIntoFlow(
  document: CanvasDocument,
  previous: CanvasFlowState
): CanvasFlowState {
  const next = canvasDocumentToFlow(document);
  const previousNodes = new Map(previous.nodes.map((node) => [node.id, node]));
  const previousEdges = new Map(previous.edges.map((edge) => [edge.id, edge]));
  const merged = {
    ...next,
    nodes: next.nodes.map((node) => {
      const previousNode = previousNodes.get(node.id);
      const preservesGeometry = previousNode !== undefined &&
        previousNode.data.canvasNode.size.width === node.data.canvasNode.size.width &&
        previousNode.data.canvasNode.size.height === node.data.canvasNode.size.height &&
        previousNode.data.canvasNode.collapsed === node.data.canvasNode.collapsed;
      return reuseShallow({
        ...node,
        position: reuseShallow(node.position, previousNode?.position),
        data: reuseShallow(node.data, previousNode?.data),
        ...(previousNode?.measured === undefined || !preservesGeometry ? {} : { measured: previousNode.measured }),
        ...(previousNode?.selected === undefined ? {} : { selected: previousNode.selected })
      }, previousNode);
    }),
    edges: next.edges.map((edge) => {
      const previousEdge = previousEdges.get(edge.id);
      const selected = previousEdge?.selected;
      return reuseShallow({ ...edge, data: reuseShallow(edge.data!, previousEdge?.data), ...(selected === undefined ? {} : { selected }) }, previousEdge);
    })
  };
  merged.nodes = reuseShallow(merged.nodes, previous.nodes);
  merged.edges = reuseShallow(merged.edges, previous.edges);
  merged.viewport = reuseShallow(merged.viewport, previous.viewport);
  return reuseShallow(merged, previous);
}

export function withFlowViewport(
  flow: CanvasFlowState,
  viewport: Viewport
): CanvasFlowState {
  return { ...flow, viewport };
}

export function decorateSelectionEdges(
  flow: CanvasFlowState,
  options: { bulkSourceNodeIds?: readonly string[] } = {}
): CanvasFlowState {
  const selectedNodeIds = new Set(flow.nodes.filter((node) => node.selected).map((node) => node.id));
  const selectedEdgeIds = new Set(flow.edges.filter((edge) => edge.selected).map((edge) => edge.id));
  const bulkSourceNodeIds = new Set(options.bulkSourceNodeIds ?? []);
  if (selectedNodeIds.size === 0 && selectedEdgeIds.size === 0 && bulkSourceNodeIds.size === 0) return flow;
  const relatedNodeIds = new Set<string>();
  const edges = flow.edges.map((edge) => {
    const selected = selectedEdgeIds.has(edge.id);
    const related = selected || selectedNodeIds.has(edge.source) || selectedNodeIds.has(edge.target);
    if (related) {
      relatedNodeIds.add(edge.source);
      relatedNodeIds.add(edge.target);
    }
    return {
      ...edge,
      className: selected ? "studio-edge--selected" : related ? "studio-edge--related" : "studio-edge--muted",
      ...(selected
        ? { markerEnd: { type: MarkerType.ArrowClosed, color: "#f0c15d" } }
        : related
          ? { markerEnd: { type: MarkerType.ArrowClosed, color: "#a89cff" } }
          : {})
    };
  });
  return {
    ...flow,
    nodes: flow.nodes.map((node) => {
      const { className: _className, ...rest } = node;
      const classNames = [
        relatedNodeIds.has(node.id) ? "studio-flow-node--related" : undefined,
        bulkSourceNodeIds.has(node.id) ? "studio-flow-node--bulk-source" : undefined
      ].filter(Boolean).join(" ");
      return classNames ? { ...rest, className: classNames } : rest;
    }),
    edges
  };
}

export function flowDiffersFromDocument(
  flow: CanvasFlowState,
  document: CanvasDocument
): boolean {
  // Browsing alone is not an edit, including when followed by a no-op drag end.
  // Actual graph edits still serialize the live viewport via flowToCanvasDocument.
  return JSON.stringify({ ...flowToCanvasDocument(flow), viewport: document.viewport }) !== JSON.stringify(document);
}
